import L from "leaflet";
import { escapeHtml } from "../core/support";
import type { WktGeometry } from "../geo/wkt";
import { zIndexBands } from "../sources/types";
import type { FeatureIndex } from "./FeatureIndex";
import { baseStyle, haloStyle, mergeStyle, resolveStyle } from "./style";
import type { Feature, FeatureStyle, LatLngTuple, RenderState } from "./types";

export const haloPane = "fxmap-halo";

export const featureAttribute = "data-fxmap-feature";

export type RendererLayerOptions = {
  pane: string;
  group: L.LayerGroup;
  style: Partial<FeatureStyle>;
  color: string;
};

export type FeatureRendererOptions = {
  map: L.Map;
  index: FeatureIndex;
  animate?: boolean;
  layerColor?: (typeIdName: string) => string | null;
  onClick?: (feature: Feature, latlng: LatLngTuple, originalEvent: MouseEvent | null) => void;
  onHover?: (feature: Feature | null) => void;
  onClusterClick?: (feature: Feature, latlng: LatLngTuple) => void;
};

type Rendered = {
  feature: Feature;
  layerId: string;
  style: FeatureStyle;
  layers: L.Layer[];
  paths: L.Path[];
  markers: L.Marker[];
  halo: L.Layer[];
};

type LayerRuntime = {
  id: string;
  options: RendererLayerOptions;
  renderer: L.Renderer;
  features: Feature[];
  rendered: Rendered[];
  bounds: L.LatLngBounds | null;
};

export function clusterSize(count: number): number {
  return count >= 100 ? 44 : count >= 10 ? 38 : 32;
}

export function conicRing(segments: { color: string; count: number }[]): string {
  const total = segments.reduce((sum, a) => sum + a.count, 0);
  let angle = 0;
  const stops: string[] = [];

  for (const segment of segments) {
    const next = total === 0 ? angle : angle + (segment.count / total) * 360;

    stops.push(`${segment.color} ${angle.toFixed(2)}deg ${next.toFixed(2)}deg`);
    angle = next;
  }

  return `conic-gradient(${stops.join(", ")})`;
}

export class FeatureRenderer {
  private readonly map: L.Map;
  private readonly index: FeatureIndex;
  private readonly options: FeatureRendererOptions;
  private readonly layers = new Map<string, LayerRuntime>();
  private readonly byKey = new Map<string, Rendered>();
  private readonly selected = new Set<string>();
  private readonly haloRenderer: L.Renderer;
  private hoveredKey: string | null = null;
  private disposed = false;

  constructor(options: FeatureRendererOptions) {
    this.map = options.map;
    this.index = options.index;
    this.options = options;

    if (!this.map.getPane(haloPane)) {
      const pane = this.map.createPane(haloPane);

      pane.style.zIndex = String(zIndexBands.vector - 1);
      pane.style.pointerEvents = "none";
    }

    this.haloRenderer = L.svg({ pane: haloPane });
  }

  addLayer(layerId: string, options: RendererLayerOptions): void {
    this.removeLayer(layerId);
    this.layers.set(layerId, { id: layerId, options, renderer: L.svg({ pane: options.pane }), features: [], rendered: [], bounds: null });
  }

  hasLayer(layerId: string): boolean {
    return this.layers.has(layerId);
  }

  removeLayer(layerId: string): void {
    const runtime = this.layers.get(layerId);

    if (runtime === undefined) return;

    this.clearRuntime(runtime);
    this.index.remove(layerId);
    this.layers.delete(layerId);
  }

  setFeatures(layerId: string, features: Feature[]): void {
    const runtime = this.layers.get(layerId);

    if (runtime === undefined) return;

    this.clearRuntime(runtime);
    runtime.features = [...features];
    this.index.set(layerId, runtime.features);
    this.draw(runtime);
  }

  features(layerId: string): Feature[] {
    return this.layers.get(layerId)?.features ?? [];
  }

  bounds(layerId: string): L.LatLngBounds | null {
    return this.layers.get(layerId)?.bounds ?? null;
  }

  setLayerStyle(layerId: string, style: Partial<FeatureStyle>, color: string): void {
    const runtime = this.layers.get(layerId);

    if (runtime === undefined) return;

    runtime.options.style = style;
    runtime.options.color = color;
    this.redraw(runtime);
  }

  selectedKeys(): string[] {
    return [...this.selected];
  }

  setSelected(keys: Iterable<string>): void {
    const next = new Set(keys);
    const changed = new Set([...this.selected, ...next]);

    this.selected.clear();

    for (const key of next) this.selected.add(key);

    for (const key of changed) {
      const rendered = this.byKey.get(key);

      if (rendered !== undefined) this.applyState(rendered);
    }
  }

  hovered(): string | null {
    return this.hoveredKey;
  }

  setHovered(key: string | null): void {
    if (this.hoveredKey === key) return;

    const previous = this.hoveredKey === null ? undefined : this.byKey.get(this.hoveredKey);

    this.hoveredKey = key;

    if (previous !== undefined) this.applyState(previous);

    const current = key === null ? undefined : this.byKey.get(key);

    if (current !== undefined) this.applyState(current);

    this.options.onHover?.(current?.feature ?? null);
  }

  bringToFront(key: string): void {
    const rendered = this.byKey.get(key);

    if (rendered === undefined) return;

    for (const path of rendered.paths) path.bringToFront();
    for (const marker of rendered.markers) marker.setZIndexOffset(1000);
  }

  elementOf(key: string): Element | null {
    const rendered = this.byKey.get(key);

    if (rendered === undefined) return null;

    for (const marker of rendered.markers) {
      const element = marker.getElement();

      if (element) return element;
    }

    for (const path of rendered.paths) {
      const element = path.getElement();

      if (element) return element;
    }

    return null;
  }

  dispose(): void {
    if (this.disposed) return;

    this.disposed = true;

    for (const runtime of [...this.layers.values()]) this.removeLayer(runtime.id);

    this.selected.clear();
    this.hoveredKey = null;
  }

  private redraw(runtime: LayerRuntime): void {
    this.clearRuntime(runtime);
    this.draw(runtime);
  }

  private clearRuntime(runtime: LayerRuntime): void {
    for (const rendered of runtime.rendered) {
      for (const halo of rendered.halo) halo.remove();
      for (const layer of rendered.layers) runtime.options.group.removeLayer(layer);

      this.byKey.delete(rendered.feature.key);

      if (this.hoveredKey === rendered.feature.key) this.hoveredKey = null;
    }

    runtime.rendered = [];
    runtime.bounds = null;
  }

  private draw(runtime: LayerRuntime): void {
    const bounds = L.latLngBounds([]);

    for (const feature of runtime.features) {
      const style = mergeStyle(baseStyle(runtime.options.color), runtime.options.style, feature.style);
      const rendered: Rendered = { feature, layerId: runtime.id, style, layers: [], paths: [], markers: [], halo: [] };

      if (feature.cluster) {
        this.drawCluster(rendered, runtime);
        bounds.extend(feature.anchor);
      } else {
        this.drawGeometry(rendered, runtime, feature.geometry);

        for (const point of pointsOf(feature.geometry)) bounds.extend(point);
      }

      runtime.rendered.push(rendered);
      this.byKey.set(feature.key, rendered);
      this.applyState(rendered);
    }

    runtime.bounds = bounds.isValid() ? bounds : null;
  }

  private stateOf(rendered: Rendered): RenderState {
    if (this.selected.has(rendered.feature.key)) return "selected";
    if (this.hoveredKey === rendered.feature.key) return "hover";

    return "idle";
  }

  private applyState(rendered: Rendered): void {
    const state = this.stateOf(rendered);
    const style = resolveStyle(rendered.style, state);
    const halo = haloStyle(rendered.style, state);

    for (const marker of rendered.markers) {
      const element = marker.getElement();
      const inner = element?.querySelector<HTMLElement>(".fxmap-marker, .fxmap-cluster");

      inner?.classList.toggle("fxmap-marker-hover", state === "hover");
      inner?.classList.toggle("fxmap-marker-selected", state === "selected");
      inner?.setAttribute("data-fxmap-state", state);
      marker.setZIndexOffset(state === "selected" ? 1000 : state === "hover" ? 500 : 0);
    }

    for (const path of rendered.paths) {
      path.setStyle({ color: style.color, weight: style.weight, opacity: style.opacity, fillColor: style.fillColor, fillOpacity: style.fillOpacity, dashArray: style.dashArray ?? undefined });

      const element = path.getElement();

      element?.classList.toggle("fxmap-path-hover", state === "hover");
      element?.classList.toggle("fxmap-path-selected", state === "selected");
      element?.setAttribute("data-fxmap-state", state);

      if (state === "selected") path.bringToFront();
    }

    for (const layer of rendered.halo) {
      if (halo === null) {
        if (this.map.hasLayer(layer)) layer.remove();
        continue;
      }

      if (layer instanceof L.Path) layer.setStyle({ color: halo.color, weight: halo.weight, opacity: halo.opacity });
      if (layer instanceof L.CircleMarker) layer.setRadius(halo.radius);
      if (!this.map.hasLayer(layer)) layer.addTo(this.map);
    }
  }

  private wire(layer: L.Layer, rendered: Rendered): void {
    layer.on("mouseover", () => this.setHovered(rendered.feature.key));
    layer.on("mouseout", () => {
      if (this.hoveredKey === rendered.feature.key) this.setHovered(null);
    });
    layer.on("click", event => {
      const mouse = event as L.LeafletMouseEvent;

      L.DomEvent.stopPropagation(mouse);

      const latlng: LatLngTuple = [mouse.latlng.lat, mouse.latlng.lng];

      if (rendered.feature.cluster) {
        this.options.onClusterClick?.(rendered.feature, latlng);
        return;
      }

      this.options.onClick?.(rendered.feature, latlng, (mouse.originalEvent as MouseEvent | undefined) ?? null);
    });
  }

  private markerIcon(rendered: Rendered): L.DivIcon {
    const style = rendered.style;
    const key = escapeHtml(rendered.feature.key);

    if (style.iconUrl !== null && style.mark === "icon") {
      const size = style.iconSize ?? [24, 24];
      const anchor = style.iconAnchor ?? [size[0] / 2, size[1] / 2];

      return L.divIcon({
        className: "fxmap-marker-wrap",
        html: `<div class="fxmap-marker fxmap-marker-icon" ${featureAttribute}="${key}" style="width:${size[0]}px;height:${size[1]}px"><img src="${escapeHtml(style.iconUrl)}" alt="" draggable="false" width="${size[0]}" height="${size[1]}"></div>`,
        iconSize: [size[0], size[1]],
        iconAnchor: [anchor[0], anchor[1]]
      });
    }

    const diameter = Math.max(6, style.radius * 2);

    return L.divIcon({
      className: "fxmap-marker-wrap",
      html: `<div class="fxmap-marker fxmap-marker-dot fxmap-marker-${style.mark ?? "circle"}" ${featureAttribute}="${key}" style="--fxmap-marker-color:${escapeHtml(style.fillColor)};--fxmap-marker-stroke:${escapeHtml(style.color)};width:${diameter}px;height:${diameter}px"></div>`,
      iconSize: [diameter, diameter],
      iconAnchor: [diameter / 2, diameter / 2]
    });
  }

  private clusterIcon(rendered: Rendered, runtime: LayerRuntime): L.DivIcon {
    const cluster = rendered.feature.cluster!;
    const size = clusterSize(cluster.count);
    const types = Object.keys(cluster.typeCounts).filter(a => (cluster.typeCounts[a] ?? 0) > 0);
    const mixed = types.length > 1;
    const ring = mixed ? conicRing(types.map(type => ({ color: this.options.layerColor?.(type) ?? runtime.options.color, count: cluster.typeCounts[type] ?? 0 }))) : "";
    const style = `--fxmap-cluster-color:${escapeHtml(runtime.options.color)};${mixed ? `--fxmap-cluster-ring:${ring};` : ""}width:${size}px;height:${size}px`;

    return L.divIcon({
      className: "fxmap-cluster-wrap",
      html: `<div class="fxmap-cluster${mixed ? " fxmap-cluster-mixed" : ""}" ${featureAttribute}="${escapeHtml(rendered.feature.key)}" data-fxmap-cluster="${cluster.count}" style="${style}"><span>${cluster.count}</span></div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
  }

  private drawCluster(rendered: Rendered, runtime: LayerRuntime): void {
    const marker = L.marker(rendered.feature.anchor, { icon: this.clusterIcon(rendered, runtime), keyboard: false, pane: runtime.options.pane });

    this.wire(marker, rendered);
    marker.addTo(runtime.options.group);
    rendered.layers.push(marker);
    rendered.markers.push(marker);
  }

  private tagElement(path: L.Path, rendered: Rendered, className: string): void {
    const tag = () => {
      const element = path.getElement();

      if (!element) return;

      element.setAttribute(featureAttribute, rendered.feature.key);
      element.classList.add(className);
    };

    path.on("add", tag);
    tag();
  }

  private drawGeometry(rendered: Rendered, runtime: LayerRuntime, geometry: WktGeometry): void {
    const style = rendered.style;
    const pane = runtime.options.pane;
    const halo = haloStyle(style, "selected")!;

    switch (geometry.kind) {
      case "point": {
        const marker = L.marker(geometry.point, { icon: this.markerIcon(rendered), keyboard: false, pane, riseOnHover: true });
        const ring = L.circleMarker(geometry.point, { pane: haloPane, renderer: this.haloRenderer, radius: halo.radius, color: halo.color, weight: 3, opacity: halo.opacity, fill: false, interactive: false, className: "fxmap-feature-halo" });

        this.wire(marker, rendered);
        marker.addTo(runtime.options.group);
        rendered.layers.push(marker);
        rendered.markers.push(marker);
        rendered.halo.push(ring);
        return;
      }
      case "line": {
        const line = L.polyline(geometry.points, {
          color: style.color,
          weight: style.weight,
          opacity: style.opacity,
          dashArray: style.dashArray ?? undefined,
          lineCap: style.lineCap,
          pane,
          renderer: runtime.renderer,
          className: "fxmap-feature-line"
        });
        const casing = L.polyline(geometry.points, { color: halo.color, weight: halo.weight, opacity: halo.opacity, pane: haloPane, renderer: this.haloRenderer, interactive: false, className: "fxmap-feature-halo" });

        this.tagElement(line, rendered, "fxmap-feature-line");
        this.wire(line, rendered);
        line.addTo(runtime.options.group);
        rendered.layers.push(line);
        rendered.paths.push(line);
        rendered.halo.push(casing);
        return;
      }
      case "polygon": {
        const polygon = L.polygon(geometry.rings, {
          color: style.color,
          weight: style.weight,
          opacity: style.opacity,
          dashArray: style.dashArray ?? undefined,
          fill: style.fill,
          fillColor: style.fillColor,
          fillOpacity: style.fillOpacity,
          pane,
          renderer: runtime.renderer,
          className: "fxmap-feature-polygon"
        });
        const casing = L.polygon(geometry.rings, { color: halo.color, weight: halo.weight, opacity: halo.opacity, fill: false, pane: haloPane, renderer: this.haloRenderer, interactive: false, className: "fxmap-feature-halo" });

        this.tagElement(polygon, rendered, "fxmap-feature-polygon");
        this.wire(polygon, rendered);
        polygon.addTo(runtime.options.group);
        rendered.layers.push(polygon);
        rendered.paths.push(polygon);
        rendered.halo.push(casing);
        return;
      }
      case "multi":
        for (const part of geometry.parts) this.drawGeometry(rendered, runtime, part);
    }
  }
}

function pointsOf(geometry: WktGeometry): LatLngTuple[] {
  switch (geometry.kind) {
    case "point":
      return [geometry.point];
    case "line":
      return geometry.points;
    case "polygon":
      return geometry.rings.flat();
    case "multi":
      return geometry.parts.flatMap(pointsOf);
  }
}
